import { Typography } from "@mui/material";
import { useState } from "react";
import {
  Datagrid,
  FunctionField,
  Link,
  List,
  TextField,
  useLocaleState,
  useRecordContext,
  useTranslate,
} from "react-admin";

import { ROWS_PER_PAGE } from "../constants";
import { formatDateTimeInline } from "../lib/dateUtils";
import { formatName } from "../lib/formatName";
import { getProcedureDocumentation, getProcedureDocumentationSyncs } from "../lib/procedureDocumentation";
import { procedureShowPath } from "../lib/procedureRoutes";
import { PatientRecord } from "../types/patient";
import { ProcedureRecord } from "../types/procedure";
import { DocumentationButtonField } from "./fields/DocumentationButtonField";
import { DocumentationSyncStatus } from "./fields/DocumentationSyncStatus";
import { ProcedureStatusChip } from "./fields/ProcedureStatusChip";
import { InfoDialog } from "./InfoDialog";

const OtherPatientsField = ({ currentPatientId }: { currentPatientId: string }) => {
  const procedureRecord = useRecordContext<ProcedureRecord>();

  if (!procedureRecord) return null;

  // Get all participating patients
  const allPatients =
    procedureRecord.procedureParticipants?.nodes
      ?.map(participant => participant.patient)
      .filter((patient): patient is NonNullable<typeof patient> => patient !== null && patient !== undefined) || [];

  // Filter out the current patient (the one whose detail page we're on)
  const otherPatients = allPatients.filter(patient => patient.id !== currentPatientId);

  if (otherPatients.length === 0) {
    return <span>-</span>;
  }

  if (otherPatients.length === 1) {
    const patient = otherPatients[0];
    const patientName = formatName(patient.user);
    return <Link to={`/Patient/${patient.id}/show`}>{patientName}</Link>;
  }

  // For multiple other patients, show the first one with a count
  const firstPatient = otherPatients[0];
  const firstPatientName = formatName(firstPatient.user);

  return (
    <Link to={`/Patient/${firstPatient.id}/show`}>
      {firstPatientName} (+{otherPatients.length - 1} more)
    </Link>
  );
};

export const PatientProcedureList = () => {
  const record = useRecordContext<PatientRecord>();
  const translate = useTranslate();
  const [locale] = useLocaleState();
  const [modalState, setModalState] = useState<{ title: string; content: string } | null>();

  if (!record) {
    return (
      <Typography variant="body2" color="text.secondary">
        {translate("procedureList.noDataAvailable")}
      </Typography>
    );
  }

  return (
    <List
      resource="Procedure"
      filter={{ "patient.id": record.id }}
      actions={false}
      title={translate("procedureList.title")}
      empty={<Typography variant="body1">{translate("procedureList.noProcedures")}</Typography>}
      perPage={ROWS_PER_PAGE}
      sort={{ field: "plannedStart", order: "DESC" }}
    >
      <Datagrid bulkActionButtons={false} rowClick={id => procedureShowPath(String(id), record.id)}>
        <ProcedureStatusChip />
        <FunctionField
          label={translate("Procedure.fields.plannedStart")}
          render={(procedure: ProcedureRecord) => formatDateTimeInline(procedure.plannedStart, locale)}
        />
        <TextField source="codeDisplay" label={translate("Procedure.fields.codeDisplay")} sortable={false} />
        <FunctionField
          label={translate("Procedure.fields.otherPatients")}
          render={() => <OtherPatientsField currentPatientId={record.id} />}
        />
        <FunctionField
          label={translate("Procedure.fields.performedStart")}
          render={(procedure: ProcedureRecord) => formatDateTimeInline(procedure.performedStart, locale)}
        />
        <FunctionField
          label={translate("Procedure.fields.performedEnd")}
          render={(procedure: ProcedureRecord) => formatDateTimeInline(procedure.performedEnd, locale)}
        />
        <FunctionField
          label={translate("Procedure.fields.performer")}
          render={(procedure: ProcedureRecord) => formatName(procedure.performer?.user)}
        />
        <DocumentationButtonField
          label={translate("Procedure.fields.documentation")}
          getContent={procedure => getProcedureDocumentation(procedure, record.userId)}
          onDocumentationClick={(title, content) => setModalState({ title, content })}
        />
        <FunctionField
          label={translate("Procedure.fields.documentationSync")}
          render={(procedure: ProcedureRecord) => (
            <DocumentationSyncStatus syncs={getProcedureDocumentationSyncs(procedure, record.userId)} />
          )}
        />
      </Datagrid>
      {modalState && (
        <InfoDialog title={modalState.title} content={modalState.content} onClose={() => setModalState(null)} />
      )}
    </List>
  );
};
